import { InsurerContacts } from '@/components/_common/MultipleContactModal/types';
import { useServiceError } from '@/hooks';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  detailInsurerContacts,
  getInsurer,
  getInsurerContacts,
  postInsurerContacts,
  putInsurerContacts,
  removeInsurerContactDetail,
  removeInsurerContacts,
} from './InsurersContact.service';
import Authorities from '@/types/Authority';
import { queryClient } from '@/Root';
import { InsurerContact } from './InsurersContact.types';
import notificationError from '@/utils/functions/notificationError';
import { hasPermissions } from '@/utils/functions/hasPermissions';

export function useInsurer(id?: number) {
  const query = useQuery(['insurer', id], () => (id ? getInsurer(id) : null), {
    enabled: !!id,
  });

  useServiceError(query.error);

  return query;
}

export function useInsurerContactList(insurerId?: number, enabled = true) {
  const query = useQuery(
    ['insurers/contacts', insurerId],
    () => (!!insurerId ? getInsurerContacts(insurerId) : []),
    {
      enabled:
        (hasPermissions([Authorities.REGISTER_INSURER_EDIT]) && !!insurerId) ||
        enabled,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useInsurerContactListMutate() {
  const cache = queryClient.getQueryData<InsurerContact[]>([
    'insurers/contacts/mutate',
  ]);

  return useMutation(
    async (insurerId: number) => {
      if (!hasPermissions([Authorities.REGISTER_INSURER_EDIT])) return;

      if (cache) return cache;

      return await getInsurerContacts(insurerId);
    },
    {
      onSuccess(data) {
        queryClient.setQueryData(['insurers/contacts/mutate'], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useInsurerContactCreate() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data: { value: InsurerContacts }) => postInsurerContacts(data),
    {
      onSuccess() {
        queryClient.invalidateQueries('insurers/contacts');

        notification.success({
          message: intl({ id: 'commons.modal.contact.added.message' }),
          description: intl({
            id: 'commons.modal.contact.added.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useInsurerContactEdit() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: InsurerContacts) => putInsurerContacts(obj),
    {
      onSuccess() {
        queryClient.invalidateQueries('insurers/contacts');

        notification.success({
          message: intl({ id: 'commons.modal.contact.updated.message' }),
          description: intl({
            id: 'commons.modal.contact.updated.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useInsurerContactDetail() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data: { insurerId: number; contactId: number }) =>
      detailInsurerContacts(data.insurerId, data.contactId),
    {
      onSuccess() {
        queryClient.invalidateQueries('insurers/contacts');

        notification.success({
          message: intl({ id: 'commons.modal.contact.updated.message' }),
          description: intl({
            id: 'commons.modal.contact.updated.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useInsurerContactRemove() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data: { insurerId: number; contactId: number }) =>
      removeInsurerContacts(data.insurerId, data.contactId),
    {
      onSuccess() {
        queryClient.invalidateQueries('insurers/contacts');

        notification.success({
          message: intl({ id: 'commons.modal.contact.removed.message' }),
          description: intl({
            id: 'commons.modal.contact.removed.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useInsurerContactDetailRemove() {
  const intl = useIntl().formatMessage;

  const mutation = useMutation(
    (data: { insurerId: number; detailId: number }) =>
      removeInsurerContactDetail(data.insurerId, data.detailId),
    {
      onSuccess() {
        notification.success({
          message: intl({ id: 'commons.modal.contact.removed.message' }),
          description: intl({
            id: 'commons.modal.contact.removed.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
